"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { BdCard, BdWallet2 } from "solar-icon-react/bd";
import { cn, formatNumber } from "@/lib/utils";
import { useSettings } from "@/hooks/useSettings";
import {
  IDefaultPackage,
  IPackages,
  TServiceNavigationItem,
} from "@/interfaces";
import { CustomButton, ErrorMessage } from "../ui";
import Timer from "@/components/timer";
import styles from "./paymentCheckout.module.css";

interface Props {
  onClose: () => void;
  onSubmit: (data: { bank?: string; digit?: string; topup?: boolean }) => void;
  serviceType?: TServiceNavigationItem | string;
  walletBalance?: string;
  inquiryPrice?: string;
  showManualError?: string | null;
  error?: Error | null;
  isLoading: boolean;
  paymentType?: "prepaid" | "postpaid";
  payBtnId?: string;
  selectedPackage?: IDefaultPackage | IPackages;
  useWallet: boolean;
  walletHandler: (value: boolean) => void;
  hasPackage?: boolean;
  isTimerActive?: boolean;
  onTimerComplete?: () => void;
  timerEndTime?: number | null;
  btnTitle?: string;
}

export default function SufficientBalanceModal({
  onClose,
  onSubmit,
  serviceType,
  walletBalance,
  inquiryPrice,
  error,
  isLoading,
  paymentType,
  showManualError = null,
  payBtnId = "payment-method-modal-submit",
  selectedPackage,
  useWallet,
  walletHandler,
  hasPackage = false,
  isTimerActive,
  onTimerComplete,
  timerEndTime = null,
  btnTitle,
}: Props) {
  const { data: session } = useSession();
  const { gateways: availableGateways, hasActiveGateways } = useSettings(
    session?.user.token,
  );
  const [selectedBank, setSelectedBank] = useState<string>();
  const [isTopupOpen, setIsTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState("50000");
  const [topupError, setTopupError] = useState<string>();
  const isSingleInquiry = selectedPackage?.id === "p0";
  const selectedPackageAmount = Number(
    selectedPackage?.price?.replace(/[^\d]/g, "") || 0,
  );
  const inquiryAmount = Number(inquiryPrice || 0);
  const originalPackageAmount =
    selectedPackage && "inquiryPrice" in selectedPackage
      ? Number(selectedPackage.inquiryPrice || selectedPackageAmount)
      : selectedPackageAmount;
  const originalAmount = isSingleInquiry
    ? inquiryAmount
    : originalPackageAmount;
  const packageDiscount =
    isSingleInquiry || hasPackage
      ? 0
      : Math.max(0, originalPackageAmount - selectedPackageAmount);
  const remainingAmount = useWallet
    ? Math.max(0, selectedPackageAmount - Number(walletBalance || 0))
    : selectedPackageAmount;
  const totalAmount = isSingleInquiry
    ? inquiryAmount
    : hasPackage
      ? 0
      : remainingAmount;
  const actionLabel =
    btnTitle ||
    (hasPackage || useWallet
      ? "ثبت استعلام"
      : `پرداخت ${formatNumber(totalAmount)} تومان`);
  const walletBalanceAmount = Number(walletBalance || 0);
  const walletRequiredAmount = isSingleInquiry
    ? inquiryAmount
    : hasPackage
      ? 0
      : selectedPackageAmount;
  const walletCanPay =
    paymentType === "postpaid" || walletRequiredAmount <= walletBalanceAmount;
  const shouldShowPaymentMethods = !hasPackage;
  const shouldShowGatewaySelection =
    !hasPackage && !useWallet && paymentType !== "postpaid";

  useEffect(() => {
    if (!availableGateways.length) return;
    const defaultBank =
      availableGateways.find((item) => item.isSelected)?.key ||
      availableGateways[0].key;
    setSelectedBank(defaultBank);
  }, [availableGateways]);

  useEffect(() => {
    // Prefer wallet payment whenever its balance covers the order or the
    // account is postpaid. Switching to direct payment remains user-controlled.
    walletHandler(walletCanPay);
  }, [walletCanPay, walletHandler]);

  useEffect(() => {
    // A package change starts a new order, so it must not retain the previous
    // wallet top-up amount in the main payment action.
    setIsTopupOpen(false);
    setTopupError(undefined);
  }, [selectedPackage?.id]);

  return (
    <>
      <div>
        <div className={styles.summaryTop}>
          <h3>خلاصه سفارش</h3>
          <span>همه مبالغ به تومان</span>
        </div>
        <div className={styles.receipt}>
          <div className={styles.receiptTitle}>
            <span className={styles.receiptIcon}>
              <BdWallet2 size={21} />
            </span>
            <div>
              <strong>
                {typeof serviceType === "string"
                  ? serviceType
                  : serviceType?.label || "استعلام"}
              </strong>
              <small>۱ استعلام · بدون محدودیت بسته</small>
            </div>
          </div>
          <div className={styles.receiptRow}>
            <span>مبلغ اولیه</span>
            <b>{formatNumber(originalAmount)} تومان</b>
          </div>
          <div className={styles.receiptRow}>
            <span>تخفیف بسته</span>
            <b>{formatNumber(packageDiscount)} تومان</b>
          </div>
          <div className={styles.receiptTotal}>
            <strong>مبلغ قابل پرداخت</strong>
            <span className={styles.receiptAmount}>
              {formatNumber(totalAmount)}
              <small className={styles.currency}>تومان</small>
            </span>
          </div>
        </div>

        {shouldShowPaymentMethods && (
          <div>
            <div className={styles.paymentHeading}>
              <h3>روش پرداخت</h3>
              <span>یکی را انتخاب کنید</span>
            </div>
            <div className={styles.methods}>
              <button
                className={cn(
                  styles.method,
                  !useWallet && styles.methodSelected,
                )}
                type="button"
                disabled={!hasActiveGateways}
                onClick={() => walletHandler(false)}
              >
                <span className={styles.methodTop}>
                  <BdCard size={22} />
                  <span className={styles.methodRadio} />
                </span>
                <span className={styles.methodInfo}>
                  <strong>پرداخت مستقیم</strong>
                  <small>با کارت بانکی</small>
                </span>
              </button>
              <button
                className={cn(
                  styles.method,
                  useWallet && styles.methodSelected,
                  !walletCanPay && styles.methodDisabled,
                )}
                type="button"
                disabled={!walletCanPay}
                onClick={() => walletHandler(true)}
              >
                <span className={styles.methodTop}>
                  <BdWallet2 size={22} />
                  <span className={styles.methodRadio} />
                </span>
                <span className={styles.methodInfo}>
                  <strong>کیف پول</strong>
                  <small>
                    موجودی: {formatNumber(Number(walletBalance || 0))} تومان
                  </small>
                </span>
              </button>
            </div>
          </div>
        )}
        {!hasPackage && (
          <div className={styles.balanceCard}>
            <div className={styles.balanceMain}>
              <BdWallet2 size={18} />
              <span>موجودی کیف پول</span>
              <b>{formatNumber(Number(walletBalance || 0))} تومان</b>
            </div>
            <button
              className={styles.topupLink}
              type="button"
              onClick={() => setIsTopupOpen((current) => !current)}
            >
              {isTopupOpen ? "− بستن شارژ" : "+ افزایش اعتبار"}
            </button>
          </div>
        )}
        {isTopupOpen && !hasPackage && (
          <div className={styles.topup}>
            <div className={styles.paymentHeading} style={{ marginTop: 0 }}>
              <h3>افزایش اعتبار کیف پول</h3>
              <span>بستن ×</span>
            </div>
            <p className={styles.topupDescription}>
              مبلغ شارژ به موجودی شما اضافه می‌شود.
            </p>
            <div className={styles.amountChoices}>
              {["50000", "80000", "200000"].map((amount) => (
                <button
                  className={cn(
                    topupAmount === amount &&
                      "border-[#5a4bd2]! bg-[#efedff]! text-[#5a4bd2]!",
                  )}
                  key={amount}
                  type="button"
                  onClick={() => {
                    setTopupAmount(amount);
                    setTopupError(undefined);
                  }}
                >
                  {formatNumber(Number(amount))}
                </button>
              ))}
            </div>
            <div className={styles.customAmount}>
              <label htmlFor="checkout-topup-amount">مبلغ دلخواه</label>
              <input
                id="checkout-topup-amount"
                inputMode="numeric"
                value={topupAmount}
                onChange={(event) => {
                  const amount = event.target.value.replace(/\D/g, "");
                  setTopupAmount(amount);
                  if (Number(amount) >= 50000) setTopupError(undefined);
                }}
                placeholder="۵۰٬۰۰۰"
              />
              <span>تومان</span>
            </div>
            {topupError && (
              <p className="mt-2 text-xs text-red-600">{topupError}</p>
            )}
          </div>
        )}
        {shouldShowGatewaySelection && (
          <div className={styles.gatewaySection}>
            <div className={styles.paymentHeading}>
              <h3>انتخاب درگاه</h3>
              <span>یکی را انتخاب کنید</span>
            </div>
            {hasActiveGateways ? (
              <div className={styles.methods}>
                {availableGateways.map((gateway) => (
                  <button
                    key={gateway.key}
                    className={cn(
                      styles.method,
                      selectedBank === gateway.key && styles.methodSelected,
                    )}
                    type="button"
                    onClick={() => setSelectedBank(gateway.key)}
                  >
                    <span className={styles.methodTop}>
                      {gateway.icon ? (
                        <Image
                          src={gateway.icon}
                          alt={gateway.label}
                          width={22}
                          height={22}
                        />
                      ) : (
                        <BdCard size={22} />
                      )}
                      <span className={styles.methodRadio} />
                    </span>
                    <span className={styles.methodInfo}>
                      <strong>{gateway.label}</strong>
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <ErrorMessage message="هیچ درگاه پرداخت فعالی در دسترس نیست" />
            )}
          </div>
        )}
        {(showManualError || error) && (
          <div className="mt-4">
            <ErrorMessage message={showManualError || error?.message} />
          </div>
        )}
        <Timer
          isActive={isTimerActive}
          endTime={timerEndTime}
          onComplete={onTimerComplete}
        />
      </div>
      <div className={styles.actionArea}>
        <CustomButton
          className={styles.primaryButton}
          onClick={() => {
            if (isTopupOpen && Number(topupAmount) < 50000) {
              setTopupError(
                "حداقل مبلغ افزایش اعتبار کیف پول ۵۰٬۰۰۰ تومان است.",
              );
              return;
            }

            onSubmit(
              isTopupOpen
                ? { bank: selectedBank, digit: topupAmount, topup: true }
                : { bank: selectedBank },
            );
          }}
          isLoading={isLoading}
          loadingLabel="inquiring"
          id={payBtnId}
          disabled={isTimerActive}
        >
          {isTopupOpen
            ? `پرداخت ${formatNumber(Number(topupAmount || 0))} تومان`
            : actionLabel}
        </CustomButton>
        <p className={styles.actionNote}>
          بعد از شارژ، می‌توانید سفارش را از کیف پول پرداخت کنید.
        </p>
      </div>
    </>
  );
}
